/**
 * Geometry of the page stack inside the canvas scroller. These mirror
 * `.bpe-canvas__inner`'s padding and the gap `PageView` puts under each page;
 * they are here because both the zoom maths and the scroll maths need them and
 * disagreeing about them is what leaves the viewport parked between two pages.
 */
export const CANVAS_PAD_TOP = 32;
export const CANVAS_PAD_BOTTOM = 80;
export const PAGE_GAP = 28;
/** Horizontal padding either side, from the same rule. */
const CANVAS_PAD_X = 40;

/**
 * The zoom that makes a page fit the visible canvas.
 *
 * "Fit width" is what you want while writing — the page as wide as it will go.
 * "Fit page" is what you want while checking pagination, because a whole page
 * at once is the only way to see where the break actually landed. So fit-page
 * has to leave room for the whole page and nothing may round it back up: at two
 * decimal places, rounding to nearest can add half a percent of height, which
 * is enough to push the last line of an A4 page out of view and defeat the
 * entire point of the button.
 */
export function fitZoom(mode: "width" | "page", scroller: { clientWidth: number; clientHeight: number }, page: { width: number; height: number }): number {
  const byWidth = (scroller.clientWidth - CANVAS_PAD_X * 2) / page.width;
  // A page is centred vertically when it fits, so only one page's worth of
  // padding has to be accounted for, not the full top-plus-bottom.
  const byHeight = (scroller.clientHeight - CANVAS_PAD_TOP * 2) / page.height;
  const raw = mode === "width" ? byWidth : Math.min(byWidth, byHeight);
  return Math.min(2, Math.max(0.25, Math.floor(raw * 100) / 100));
}

/** Distance from the top of the scroller's content to the top of page `index`. */
export function pageTop(index: number, pageHeight: number, zoom: number): number {
  return CANVAS_PAD_TOP + index * (pageHeight * zoom + PAGE_GAP);
}

/**
 * Where to scroll so page `index` is the page you are looking at: centred when
 * it fits, top-aligned when it does not.
 */
export function scrollTopForPage(index: number, pageHeight: number, zoom: number, viewportHeight: number): number {
  const scaled = pageHeight * zoom;
  const slack = Math.max(0, (viewportHeight - scaled) / 2);
  return Math.max(0, pageTop(index, pageHeight, zoom) - slack);
}
